import { useEffect } from 'react'

import { BrandMark } from '../components/Brand'
import { Footer } from '../components/Footer'
import { Header } from '../components/Header'
import { Icon } from '../components/Icon'
import { ProfessionalEnquiryForm } from '../components/ProfessionalEnquiryForm'
import { ProfessionalFaq } from '../components/ProfessionalFaq'
import { usePageMetadata } from '../hooks/usePageMetadata'
import { useRevealAnimations } from '../hooks/useRevealAnimations'
import { recordProfessionalEvent } from '../lib/submissionApi'

const opportunityPoints = [
  {
    icon: 'shield' as const,
    title: 'CA(SA) is our primary focus',
    copy: 'CIMA professionals and people on other credible finance pathways may also enquire.',
  },
  {
    icon: 'monitor' as const,
    title: 'Fully remote from South Africa',
    copy: 'The initial model is full-time and dedicated to one international client team.',
  },
  {
    icon: 'clock' as const,
    title: 'Some roles align to US hours',
    copy: 'Approximately 14:00–22:00 or 15:00–23:00 SAST, depending on the client and daylight saving.',
  },
]

const arkHandles = [
  'Initial screening and qualification checks',
  'South African contracts and payroll',
  'Leave administration and HR support',
  'A yearly professional-development budget',
]

const clientHandles = [
  'Interviews and selection',
  'Onboarding, equipment and software',
  'Role-specific training and team integration',
  'Career development and any bonuses',
]

const process = [
  {
    title: 'Share your interest',
    copy: 'Tell us about your background, experience and the kind of opportunity you want.',
  },
  {
    title: 'Ark reviews your enquiry',
    copy: 'If there may be a fit, we contact you by email and phone for an initial screening call.',
  },
  {
    title: 'You stay in control',
    copy: 'If a role looks suitable, we ask permission before sharing identifiable information with a client.',
  },
]

function RouteGraphic() {
  return (
    <div className="professional-route" aria-hidden="true">
      <svg viewBox="0 0 720 440">
        <path className="route-orbit" d="M54 326C178 86 426 37 668 106" />
        <path className="route-main" d="M90 330C224 281 326 261 407 203S558 105 668 106" />
        <path className="route-branch" d="M407 203C365 134 287 99 196 112" />
        <circle className="route-origin" cx="90" cy="330" r="13" />
        <circle className="route-node" cx="407" cy="203" r="8" />
        <circle className="route-node" cx="196" cy="112" r="8" />
        <circle className="route-node route-node--light" cx="668" cy="106" r="10" />
      </svg>
      <div className="route-profile-card route-profile-card--origin">
        <span>South Africa</span>
        <strong>Qualified finance professional</strong>
      </div>
      <div className="route-profile-card route-profile-card--destination">
        <span>International team</span>
        <strong>Dedicated remote opportunity</strong>
      </div>
    </div>
  )
}

function ResponsibilityList({ title, items, dark = false }: { title: string; items: string[]; dark?: boolean }) {
  return (
    <article className={`responsibility-panel${dark ? ' responsibility-panel--dark' : ''}`}>
      <div className="responsibility-title">
        <span className="icon-ring"><BrandMark /></span>
        <h3>{title}</h3>
      </div>
      <ul>
        {items.map((item) => <li key={item}><Icon name="shield" />{item}</li>)}
      </ul>
    </article>
  )
}

export function ProfessionalsPage() {
  useRevealAnimations()
  usePageMetadata({
    title: 'For professionals | Remote finance opportunities | Ark Solutions',
    description: 'Enquire about full-time, dedicated remote finance opportunities from South Africa with international accounting and audit teams.',
    canonicalPath: '/for-professionals',
  })
  useEffect(() => {
    recordProfessionalEvent({ event: 'professional_page_viewed' })
  }, [])

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <div className="site-shell professional-shell" id="top">
        <Header audience="professionals" />
        <section className="professional-hero" aria-labelledby="professional-hero-title">
          <div className="container professional-hero-grid">
            <div className="professional-hero-copy">
              <h1 id="professional-hero-title">Build an international career from South Africa.</h1>
              <p>Explore possible full-time, dedicated remote roles with international accounting and audit teams—while Ark supports your employment locally.</p>
              <a className="button" href="#professional-enquiry">Share your interest <Icon name="arrow" /></a>
              <small>This is an enquiry, not registration or a guaranteed vacancy.</small>
            </div>
            <RouteGraphic />
          </div>
        </section>
      </div>

      <main id="main">
        <section className="section professional-opportunity" id="opportunity" aria-labelledby="opportunity-title">
          <div className="container">
            <div className="opportunity-intro reveal">
              <h2 className="section-title" id="opportunity-title">A serious route for ambitious finance professionals.</h2>
              <p className="section-copy">Ark connects strong South African professionals with international teams where they can contribute, grow and build long-term working relationships.</p>
            </div>
            <div className="opportunity-rail reveal">
              {opportunityPoints.map((item) => (
                <article key={item.title}>
                  <span className="icon-ring"><Icon name={item.icon} /></span>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="responsibility-section" aria-labelledby="responsibility-heading">
          <h2 className="sr-only" id="responsibility-heading">Who handles what</h2>
          <div className="container responsibility-frame reveal">
            <ResponsibilityList title="What Ark handles" items={arkHandles} />
            <ResponsibilityList title="What your client handles" items={clientHandles} dark />
          </div>
          <p className="container value-statement reveal">Our lean employment model is designed to direct more of the overall engagement value toward professional remuneration, support, and development.</p>
        </section>

        <section className="section professional-process" id="professional-process" aria-labelledby="professional-process-title">
          <div className="container">
            <div className="process-intro reveal">
              <p className="section-number">A clear process</p>
              <h2 className="section-title" id="professional-process-title">One enquiry. A considered human review.</h2>
              <p className="section-copy">Ark may not have a suitable position when you enquire. We will acknowledge every submission and communicate an outcome after review.</p>
            </div>
            <ol className="professional-process-rail reveal">
              {process.map((step, index) => (
                <li key={step.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div><h3>{step.title}</h3><p>{step.copy}</p></div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="section section--mist professional-enquiry" id="professional-enquiry" aria-labelledby="professional-enquiry-title">
          <div className="container professional-enquiry-layout">
            <div className="professional-enquiry-intro reveal">
              <p className="section-number">Share your interest</p>
              <h2 className="section-title" id="professional-enquiry-title">Tell us where you could make an impact.</h2>
              <p className="section-copy">Keep it concise. Your LinkedIn profile and a focused summary give us enough to begin; attaching a CV is optional.</p>
              <div className="enquiry-promise">
                <Icon name="lock" />
                <p><strong>Your details stay private.</strong> We do not share identifiable information with a client unless we ask and you agree separately.</p>
              </div>
            </div>
            <div className="professional-form-frame reveal">
              <ProfessionalEnquiryForm />
            </div>
          </div>
        </section>

        <ProfessionalFaq />
      </main>
      <Footer audience="professionals" />
    </>
  )
}
